import React, { useState } from 'react'
import type { MouseEvent } from 'react'
import html2canvas from 'html2canvas'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { LoadingIcon, DownloadIcon } from '@homeviews/svg'
import { Typography } from '@homeviews/shared-ui'
import { mixpanelTrack, EVENTS } from '@homeviews/seo'
import type { ITheme } from '@homeviews/styles'
import { theme } from '@homeviews/styles'

// Todo - slightly refactore code to reuse styles between ScreenshotButton and CsvExportButton components
const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      transition: 'all .2s ease-in-out',
      width: 23,
      height: 23,
      border: '1px solid #DEE1E6',
      borderRadius: '11.5px',
      overflow: 'hidden',
      justifyContent: 'flex-start!important',
      paddingLeft: 5,
      marginRight: 5,
      '&:hover': {
        cursor: 'pointer',
        width: '120px!important',
      },
      '& svg': {
        flexShrink: 0,
      },
    },
    loading: {
      width: '130px!important',
      '&:hover': {
        width: '130px!important',
      },
      '& svg': {
        animation: `spin 1000ms linear infinite`,
      },
    },
    title: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '22px',
      marginLeft: 6,
      whiteSpace: 'nowrap',
    },
  }),
)

const ScreenshotButton = () => {
  const [loading, setLoading] = useState(false)
  const classes = useStyles()
  const theme = useTheme()

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    setLoading(true)

    const card: HTMLElement = e.currentTarget.closest('.c-card')

    if (!card) {
      console.warn('Card component is not a parent')
    }

    const asAtEl = card.lastChild.firstChild.cloneNode() // Clone "Source: HomeViews" el styling
    const todayStr = new Date().toLocaleDateString('en-gb')
    asAtEl.textContent = `As at: ${todayStr}` // e.g. "As at: 15/11/2022"
    card.lastChild.appendChild(asAtEl) // Add new el adjacent-after "Source: HomeViews" just for screenshot

    const eventName = card.dataset?.eventName

    const eventKey = 'DASHBOARD_DOWNLOAD_CHART'
    let chartName = ''
    let exportType = 'PNG'

    switch (eventName) {
      case 'DEVELOPMENT_SCORES_CHART': {
        chartName = 'Development Scores'
        break
      }
      case 'REVIEWER_TYPES_CHART': {
        chartName = 'Reviewer Types'
        break
      }
      case 'SELECTED_DEVELOPMENT_SCORES_CHART': {
        chartName = 'Selected development score'
        exportType = 'CSV'
        break
      }
      case 'RATINGS_BY_REVIEWER_TYPE_CHART': {
        chartName = 'Ratings by Reviewer Type'
        break
      }
      case 'REVIEWS_HISTORY_CHART': {
        chartName = 'Reviews History'
        break
      }
      case 'SUBMITTED_REVIEW_SCORES_BY_DATE_CHART': {
        chartName = 'Submitted Review Scores by Date'
        break
      }
      case 'REVIEWER_AGES_CHART': {
        chartName = 'Reviewer Ages'
        break
      }
      case 'RATINGS_BY_AGE_RANGE_CHART': {
        chartName = 'Ratings by Age Range'
        break
      }
      case 'SUSTAINABILITY_CHART': {
        chartName =
          'Which environmentally sustainable features are most important to your residents?'
        break
      }
      case 'RATINGS_HISTORY_CHART': {
        chartName = 'Ratings History'
        break
      }
      case 'OWNER_REVIEWS_CHART': {
        chartName = 'Owner Reviews'
        break
      }

      case 'BTR_ADDITIONAL_QUESTIONS_CHART': {
        chartName = 'Build to Rent additional questions'
        break
      }
      case 'BTS_ADDITIONAL_QUESTIONS_CHART': {
        chartName = 'Build to Sell - New Build Owner additional questions'
        break
      }
      case 'PRIVATE_TENANT_ADDITIONAL_QUESTIONS_CHART': {
        chartName = 'Private tenant additional questions (Build to Rent)'
        break
      }
      case 'OWNER_ADDITIONAL_QUESTIONS_CHART': {
        chartName = 'Owner additional questions'
        break
      }
    }

    setTimeout(() => {
      const date = new Date()
      const filename = `hv-chart-${date.toLocaleTimeString('en-GB')}.png`
      void html2canvas(card, {
        onclone: (documentClone) => {
          if (eventName === 'DEVELOPMENT_SCORES_CHART') {
            const els = documentClone.getElementsByClassName('l-chartWrapper')
            const wrapperEl = els[0] as HTMLElement
            wrapperEl.style.height = '100%'
          }
        },
      }).then((canvas) => {
        saveAs(canvas.toDataURL(), filename)
        setLoading(false)

        // Remove "As at: DD/MM/YYYY" element now screenshot is taken
        card.lastChild.removeChild(asAtEl)

        if (eventName) {
          mixpanelTrack(eventKey, {
            'Chart Name': chartName,
            'Export Type': exportType,
          })
        }
      })
    }, 200)
  }

  function saveAs(uri: string, filename: string) {
    const link = document.createElement('a')
    if (typeof link.download === 'string') {
      link.href = uri
      link.download = filename

      //Firefox requires the link to be in the body
      document.body.appendChild(link)

      //simulate click
      link.click()

      //remove the link when done
      document.body.removeChild(link)
    } else {
      window.open(uri)
    }
  }

  return (
    <div
      onClick={(e) => !loading && handleClick(e)}
      className={clsx(
        'l-center',
        classes.root,
        loading && classes.loading,
        'c-screenshotButton',
      )}
      style={{
        width: 23,
      }}
      data-html2canvas-ignore="true"
    >
      {loading ? (
        <LoadingIcon style={{ color: theme.colors.grey600 }} />
      ) : (
        <DownloadIcon />
      )}

      <Typography className={classes.title}>
        {loading ? 'Generating Chart' : 'Download Chart'}
      </Typography>
    </div>
  )
}

export default ScreenshotButton
